import React, { useCallback } from 'react';
import { styled } from '@compiled/react';
import AkButton from '@atlaskit/button';
import Heading from '@atlaskit/heading';
import { ModalBody, ModalHeader, ModalFooter } from '@atlaskit/modal-dialog';
import { Inline } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import ShortcutScope from '@atlassian/jira-common-components-keyboard-shortcuts/src/shortcut-scope.tsx';
import { useIntl } from '@atlassian/jira-intl';
import { JiraModal as ModalDialog } from '@atlassian/jira-modal/src/ui/jira-modal.tsx';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import type { ToggleableFeatureKey } from '../../common/types.tsx';
import { useFeature } from '../../controllers/board-features/index.tsx';
import { useDependencyModalContext } from '../../controllers/dependency-modal-context/index.tsx';
import { useRedirect } from '../../controllers/use-redirect/index.tsx';
import { useShowFlags } from '../../controllers/use-show-flags/index.tsx';
import messages from './messages.tsx';
import type { DisablePrerequisiteModalProps } from './types.tsx';

export const DisablePrerequisiteModal = ({
	boardId,
	projectUrl,
}: DisablePrerequisiteModalProps) => {
	const { formatMessage } = useIntl();
	const { data, closeModal } = useDependencyModalContext();
	const { showErrorFlag } = useShowFlags(projectUrl);
	const [, { updateFeature }] = useFeature();
	const redirect = useRedirect(projectUrl);
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const { featureToDisable, beneficiaryFeature } = data ?? {};

	const handleConfirm = useCallback(() => {
		updateFeature({
			boardId,
			// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
			feature: featureToDisable?.feature as ToggleableFeatureKey,
			// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
			dependentFeature: beneficiaryFeature?.feature as ToggleableFeatureKey,
			onError: showErrorFlag,
			onAfterUpdate: redirect,
			analyticsEvent: createAnalyticsEvent({}),
		});
		closeModal();
	}, [
		updateFeature,
		redirect,
		boardId,
		featureToDisable?.feature,
		beneficiaryFeature?.feature,
		showErrorFlag,
		createAnalyticsEvent,
		closeModal,
	]);

	const handleCancel = useCallback(() => {
		closeModal();
		fireUIAnalytics(createAnalyticsEvent({}), 'button clicked', 'cancelDisablePrerequisite');
	}, [closeModal, createAnalyticsEvent]);

	return (
		<ShortcutScope>
			<ModalDialog
				width="small"
				testId="tmp-feature-toggle.ui.disable-prerequisite-modal.modal-dialog"
				messageId="tmp-feature-toggle.ui.disable-prerequisite-modal.modal-dialog"
				messageType="transactional"
			>
				<ModalHeader>
					<Heading size="medium">
						{formatMessage(messages.modalTitle, {
							beneficiaryTitlesAsString: beneficiaryFeature?.localisedName,
							prerequisite: featureToDisable?.localisedName,
						})}
					</Heading>
				</ModalHeader>
				<ModalBody>
					<Description>
						{formatMessage(messages.modalDescription, {
							beneficiaryTitlesAsString: beneficiaryFeature?.localisedName,
							prerequisite: featureToDisable?.localisedName,
						})}
					</Description>
				</ModalBody>
				<ModalFooter>
					<Inline space="space.100">
						<AkButton
							appearance="primary"
							testId="tmp-feature-toggle.ui.disable-prerequisite-modal.ak-button-confirm"
							onClick={handleConfirm}
						>
							{formatMessage(messages.confirmButton)}
						</AkButton>
						<AkButton onClick={handleCancel}>{formatMessage(messages.cancelButton)}</AkButton>
					</Inline>
				</ModalFooter>
			</ModalDialog>
		</ShortcutScope>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Description = styled.span({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	color: token('color.text.subtlest'),
});
